import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { GoldButton } from "@/components/site/Ui";
import { Reveal } from "@/components/site/Reveal";
import { toast } from "sonner";
import { Wand2, Lightbulb, Loader2 } from "lucide-react";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Facebook", "Other"];

export default function VideoIdeaGenerator() {
  const [form, setForm] = useState({ name: "", email: "", niche: "", platform: "TikTok", consent: false });
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState(null);
  const [locked, setLocked] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setIdeas(null); setLocked("");
    try {
      const { data } = await api.post("/tools/video-ideas", form);
      setIdeas(data.ideas);
      toast.success("Here's your free video idea!");
    } catch (err) {
      if (err.response?.status === 403) {
        setLocked(apiErr(err.response?.data?.detail) || "You've already used your free video idea with this email.");
      } else {
        toast.error(apiErr(err.response?.data?.detail) || "Couldn't generate your idea. Try again.");
      }
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";

  return (
    <Reveal>
      <div className="rounded-3xl border border-[#D4AF37]/25 p-8 sm:p-10 grain relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#D4AF37]/10 to-transparent" />
        <div className="flex items-center gap-2 text-[#D4AF37]"><Wand2 size={20} /><span className="text-xs uppercase tracking-widest">Free tool</span></div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl">Video Idea Generator</h2>
        <p className="mt-2 text-white/60 max-w-lg">Tell me your niche and platform, and I'll instantly give you 1 clickable video idea you can film on your phone — with a title and a ready-to-use description.</p>
        <p className="mt-2 text-xs text-white/40 max-w-lg">This is a free single-use taster (one free idea per person). Our paid Content Plans give you a full month of ideas plus tags, keywords, filming guides and scripts.</p>

        <form onSubmit={submit} data-testid="idea-generator-form" className="mt-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
          <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="idea-name" className={inputCls} />
          <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="idea-email" className={inputCls} />
          <input required placeholder="Your niche (e.g. budget cooking)" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} data-testid="idea-niche" className={inputCls} />
          <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="idea-platform" className={inputCls}>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <label className="sm:col-span-2 flex items-start gap-2 text-xs text-white/55">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#D4AF37]" />
            Yes, send me helpful tips and updates by email. (Optional)
          </label>
          <GoldButton type="submit" disabled={loading} data-testid="idea-submit" className="sm:col-span-2 sm:w-auto">
            {loading ? <><Loader2 className="animate-spin" size={16} /> Thinking…</> : <><Wand2 size={16} /> Get my free idea</>}
          </GoldButton>
        </form>

        {locked && (
          <div className="mt-8 card-surface rounded-2xl p-6 text-center" data-testid="idea-locked">
            <p className="text-white/80 text-sm">{locked}</p>
            <GoldButton as="link" to="/content-plans" className="mt-4">See our Content Plans</GoldButton>
          </div>
        )}

        {ideas && ideas.length > 0 && (
          <div className="mt-8 max-w-2xl" data-testid="idea-results">
            <div className="card-surface rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] uppercase tracking-widest"><Lightbulb size={14} /> Your free video idea</div>
              <h3 className="mt-2 font-display text-xl leading-snug" data-testid="idea-title">{ideas[0].title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed" data-testid="idea-description">{ideas[0].description}</p>
            </div>
            <div className="text-center pt-5">
              <p className="text-white/50 text-sm">Want a full month of ideas with filming guides, titles, descriptions, tags and keywords?</p>
              <GoldButton as="link" to="/content-plans" variant="outline" className="mt-3">Get a Content Plan</GoldButton>
            </div>
          </div>
        )}
      </div>
    </Reveal>
  );
}
